import { useState } from 'react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import {
  Crown, Cog, DollarSign, Users, Cpu, Shield, TrendingUp,
  Sparkles, BarChart3, Truck, ShieldAlert, Award, ChevronLeft,
} from 'lucide-react';
import ExecutiveCommandCenter from '../admin/ExecutiveCommandCenter';
import HRCommandCenter from '../admin/HRCommandCenter';
import ITCommandCenter from '../admin/ITCommandCenter';
import {
  FinanceCommandCenter, LegalCommandCenter, SalesCommandCenter,
  MarketingCommandCenter, CustomerServiceCommandCenter, ProcurementCommandCenter,
  SafetyCommandCenter, TrainingCommandCenter,
} from '../admin/DeptCommandCenters';
import { canAccessDepartment, lockReasonFor } from '../../utils/departmentAccess';

/**
 * ExecutiveHQ — Master Architecture Feb 2026.
 *
 * The CEO / Admin's global 12-department cockpit, restored inside the
 * unified StaffDashboard so the migration from the deprecated
 * CompanyManagerDashboard doesn't lose any strategic surface.
 *
 * Renders as an overlay-style panel with:
 *   • A grid of 11 department tiles (Executive + 10 sub-departments)
 *   • Each tile opens the corresponding Command Center
 *   • "Back to Overview" navigates back to the tile grid
 *
 * NOTE: This is the *unscoped* view — CEO sees every department without
 * region filtering. Regional/operational scope lives in the RM Board and
 * the standard staff dashboard tabs.
 */
const DEPARTMENTS = [
  { key: 'executive',        label: 'Executive Office',   icon: Crown,       tone: '#FFD60A' },
  { key: 'operations',       label: 'Operations',         icon: Cog,         tone: '#0ea5e9' },
  { key: 'finance',          label: 'Finance',            icon: DollarSign,  tone: '#22c55e' },
  { key: 'hr',               label: 'Human Resources',    icon: Users,       tone: '#a855f7' },
  { key: 'it',               label: 'IT',                 icon: Cpu,         tone: '#06b6d4' },
  { key: 'legal',            label: 'Legal',              icon: Shield,      tone: '#f97316' },
  { key: 'sales',            label: 'Sales',              icon: TrendingUp,  tone: '#ec4899' },
  { key: 'marketing',        label: 'Marketing',          icon: Sparkles,    tone: '#eab308' },
  { key: 'customer-service', label: 'Customer Service',   icon: BarChart3,   tone: '#14b8a6' },
  { key: 'procurement',      label: 'Procurement',        icon: Truck,       tone: '#8b5cf6' },
  { key: 'safety',           label: 'Safety & Risk',      icon: ShieldAlert, tone: '#ef4444' },
  { key: 'training',         label: 'Training & Dev',     icon: Award,       tone: '#3b82f6' },
];

export function ExecutiveHQ({ user, onExit, onJumpToOps }) {
  const [dept, setDept] = useState('overview');

  const onOpenSection = (sectionKey) => {
    if (onJumpToOps) onJumpToOps(sectionKey);
    else toast.info(`Operations section "${sectionKey}" opens in the Operations tab.`);
  };

  const jumpDept = (k) => {
    if (!canAccessDepartment(user, k)) {
      toast.error(lockReasonFor(user, k) || 'You do not have access to this department.');
      return;
    }
    setDept(k);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderDept = () => {
    switch (dept) {
      case 'executive':
        return <ExecutiveCommandCenter user={user} onJumpDept={jumpDept} />;
      case 'hr':
        return <HRCommandCenter onOpenSection={onOpenSection} />;
      case 'it':
        return <ITCommandCenter onOpenSection={onOpenSection} />;
      case 'finance':          return <FinanceCommandCenter onOpenSection={onOpenSection} />;
      case 'legal':            return <LegalCommandCenter onOpenSection={onOpenSection} />;
      case 'sales':            return <SalesCommandCenter onOpenSection={onOpenSection} />;
      case 'marketing':        return <MarketingCommandCenter onOpenSection={onOpenSection} />;
      case 'customer-service': return <CustomerServiceCommandCenter onOpenSection={onOpenSection} />;
      case 'procurement':      return <ProcurementCommandCenter onOpenSection={onOpenSection} />;
      case 'safety':           return <SafetyCommandCenter onOpenSection={onOpenSection} />;
      case 'training':         return <TrainingCommandCenter onOpenSection={onOpenSection} />;
      default:
        return null;
    }
  };

  if (dept !== 'overview') {
    return (
      <div data-testid="executive-hq" className="min-h-screen bg-black">
        <div className="flex items-center gap-3 px-6 lg:px-10 pt-5">
          <Button
            onClick={() => setDept('overview')}
            variant="outline"
            className="border-[#C9A962] text-[#C9A962] bg-black hover:bg-zinc-900"
            data-testid="executive-hq-back"
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> Executive Overview
          </Button>
          <Badge className="bg-zinc-800 text-zinc-300 text-[10px]">
            {DEPARTMENTS.find((d) => d.key === dept)?.label || dept}
          </Badge>
          <div className="flex-1" />
          <Button
            onClick={onExit}
            variant="outline"
            className="border-zinc-600 text-zinc-300 hover:bg-zinc-900"
            data-testid="executive-hq-exit"
          >
            ← Back to Dashboard
          </Button>
        </div>
        {renderDept()}
      </div>
    );
  }

  return (
    <div data-testid="executive-hq" className="min-h-screen bg-black">
      <div className="px-6 lg:px-10 py-8 max-w-7xl mx-auto">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
          <div>
            <p
              className="text-[10px] uppercase tracking-[0.3em] text-[#FFD60A] font-black mb-1 flex items-center gap-1.5"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              <Crown className="h-3 w-3" />
              Executive HQ · Unscoped Access
            </p>
            <h1
              className="text-3xl sm:text-4xl font-black text-white leading-tight"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              data-testid="executive-hq-title"
            >
              12 Department Command Centers
            </h1>
            <p className="text-xs text-zinc-400 mt-2 max-w-2xl">
              Full unscoped view across every department. Regional Manager stats and daily
              operations live in the standard dashboard tabs — this is your strategic layer.
            </p>
          </div>
          <Button
            onClick={onExit}
            variant="outline"
            className="border-zinc-600 text-zinc-300 hover:bg-zinc-900"
            data-testid="executive-hq-exit-top"
          >
            ← Back to Regional Command
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="executive-hq-grid">
          {DEPARTMENTS.map((d) => {
            const Icon = d.icon;
            const locked = !canAccessDepartment(user, d.key);
            return (
              <Card
                key={d.key}
                className={`p-5 border-2 bg-zinc-950 hover:-translate-y-0.5 transition cursor-pointer ${locked ? 'opacity-50' : ''}`}
                style={{ borderColor: `${d.tone}55` }}
                onClick={() => jumpDept(d.key)}
                data-testid={`executive-hq-tile-${d.key}`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <span
                    className="h-10 w-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${d.tone}22`, color: d.tone }}
                  >
                    <Icon className="h-5 w-5" />
                  </span>
                  <p
                    className="text-sm font-bold text-white uppercase leading-tight"
                    style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                  >
                    {d.label}
                  </p>
                </div>
                <p className="text-[11px] text-zinc-400 leading-snug">
                  Live counters, escalations & inbox for the {d.label} team.
                </p>
                {locked && (
                  <p className="text-[10px] text-red-400 mt-2 uppercase tracking-widest">
                    Access restricted
                  </p>
                )}
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default ExecutiveHQ;
